// Sürücü Puantajı — sürücü performans tablosu.
// Sıralanabilir kolonlar, liderlik rozetleri, iş yükü dengesi göstergesi ve
// son 30 gün trend sparkline'ı içerir. Satıra tıklayınca detay açılır.

import React, { useMemo, useState } from "react";

import PerformanceTrend from "./PerformanceTrend";
import { formatKm, formatMinutes, formatNumber } from "../utils";

const SORT_ACCESSORS = {
  name: (r) => (r.driver.name || "").toLocaleLowerCase("tr"),
  dayCount: (r) => r.metrics.day.count,
  weekCount: (r) => r.metrics.week.count,
  monthCount: (r) => r.metrics.month.count,
  monthKm: (r) => r.metrics.month.km,
  monthMin: (r) => r.metrics.month.minutes,
};

const DriverStatsTable = ({ rows = [], avgMonth, onSelectDriver, t, showLeaders = true, periodDays = 30 }) => {
  const [sortKey, setSortKey] = useState("monthCount");
  const [sortDir, setSortDir] = useState("desc");

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.monthCount;
    const factor = sortDir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * factor;
      if (av > bv) return 1 * factor;
      return 0;
    });
  }, [rows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortArrow = (key) => (key === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "");

  // Liderlik: son 30 gün servis sayısına göre ilk 3
  const topMonthCount = useMemo(() => Math.max(0, ...rows.map((r) => r.metrics.month.count)), [rows]);
  const avgCount = avgMonth?.count || 0;

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead>
          <tr>
            <th className="dp-table__rank">#</th>
            <th className="dp-table__driver dp-table__sortable" onClick={() => handleSort("name")}>
              {t("driverPerformance.table.driver")}
              {sortArrow("name")}
            </th>
            <th className="dp-table__num dp-table__sortable" onClick={() => handleSort("dayCount")}>
              {t("driverPerformance.table.day")}
              {sortArrow("dayCount")}
            </th>
            <th className="dp-table__num dp-table__sortable" onClick={() => handleSort("weekCount")}>
              {t("driverPerformance.table.week")}
              {sortArrow("weekCount")}
            </th>
            <th className="dp-table__num dp-table__sortable" onClick={() => handleSort("monthCount")}>
              {t("driverPerformance.table.monthServices", { days: periodDays })}
              {sortArrow("monthCount")}
            </th>
            <th className="dp-table__num dp-table__sortable" onClick={() => handleSort("monthKm")}>
              {t("driverPerformance.table.monthKm", { days: periodDays })}
              {sortArrow("monthKm")}
            </th>
            <th className="dp-table__num dp-table__sortable" onClick={() => handleSort("monthMin")}>
              {t("driverPerformance.table.monthTime", { days: periodDays })}
              {sortArrow("monthMin")}
            </th>
            <th className="dp-table__balance">{t("driverPerformance.table.balance")}</th>
            <th className="dp-table__trend">{t("driverPerformance.table.trend", { days: periodDays })}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, idx) => {
            const isLeader =
              showLeaders && sortKey === "monthCount" && sortDir === "desc" && idx < 3 && row.metrics.month.count > 0;
            return (
              <tr
                key={row.driver.id}
                className="dp-table__row"
                onClick={() => onSelectDriver?.(row.driver)}
                title={t("driverPerformance.table.openDetail")}
              >
                <td className="dp-table__rank">
                  {isLeader ? <span className={`dp-rank dp-rank--${idx + 1}`}>{idx + 1}</span> : <span className="dp-rank-plain">{idx + 1}</span>}
                </td>
                <td className="dp-table__driver">
                  <span className="dp-driver-name">{row.driver.name || "-"}</span>
                  {!row.driver.isActive && <span className="dp-tag dp-tag--inactive">{t("driverPerformance.inactive")}</span>}
                  {row.metricsMissing && (
                    <span className="dp-tag dp-tag--warn" title={t("driverPerformance.metricsMissingHint")}>
                      {t("driverPerformance.metricsMissing")}
                    </span>
                  )}
                  {row.driver.phonenumber && <span className="dp-driver-phone">{row.driver.phonenumber}</span>}
                </td>
                <td className="dp-table__num">
                  <Cell count={row.metrics.day.count} km={row.metrics.day.km} minutes={row.metrics.day.minutes} />
                </td>
                <td className="dp-table__num">
                  <Cell count={row.metrics.week.count} km={row.metrics.week.km} minutes={row.metrics.week.minutes} />
                </td>
                <td className="dp-table__num dp-table__num--strong">{formatNumber(row.metrics.month.count)}</td>
                <td className="dp-table__num">{formatKm(row.metrics.month.km)}</td>
                <td className="dp-table__num">{formatMinutes(row.metrics.month.minutes)}</td>
                <td className="dp-table__balance">
                  <BalanceBar value={row.metrics.month.count} avg={avgCount} max={topMonthCount} t={t} periodDays={periodDays} />
                </td>
                <td className="dp-table__trend">
                  <PerformanceTrend series={row.dailySeries} metric="count" />
                </td>
              </tr>
            );
          })}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={9} className="dp-table__empty">
                {t("driverPerformance.noDrivers")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const Cell = ({ count, km, minutes }) => (
  <div className="dp-cell">
    <span className="dp-cell__count">{formatNumber(count)}</span>
    <span className="dp-cell__sub">
      {formatKm(km)} · {formatMinutes(minutes)}
    </span>
  </div>
);

const BalanceBar = ({ value, avg, max, t, periodDays }) => {
  if (!avg || avg <= 0) return <span className="dp-balance__na">—</span>;
  const ratio = value / avg;
  const pct = Math.round((ratio - 1) * 100);
  const width = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const state = pct > 10 ? "above" : pct < -10 ? "below" : "even";
  const label = pct > 0 ? `+${pct}%` : `${pct}%`;
  return (
    <div className="dp-balance" title={t("driverPerformance.table.balanceHint", { days: periodDays })}>
      <div className="dp-balance__track">
        <div className={`dp-balance__fill dp-balance__fill--${state}`} style={{ width: `${width}%` }} />
      </div>
      <span className={`dp-balance__label dp-balance__label--${state}`}>{label}</span>
    </div>
  );
};

export default DriverStatsTable;
